"use client";

import { LoginModal } from "@/components/auth/LoginModal";
import { UserDropdown } from "@/components/auth/UserDropdown";
import LanguageSwitcher from "@/components/shared/LanguageToggle/LanguageToggle";
import ThemeToggle from "@/components/shared/ThemeToggle/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import { Menu } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { RiArrowRightLine } from "react-icons/ri";
import CustomLink from "./CustomLink";
import { MobileMenu } from "./MobileMenu";
import NavSearchBar from "./NavSearch";
import { useHeader } from "./useHeader";

export default function Header() {
  const {
    isScrolled,
    isMobileMenuOpen,
    isLoginModalOpen,
    navLinks,
    locale,
    listLabel,
    loginLabel,
    toggleMobileMenu,
    closeMobileMenu,
    openLoginModal,
    closeLoginModal,
  } = useHeader();

  const { theme } = useTheme();

  return (
    <div className="h-18">
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500">
        <div
          className={`transition-all py-3 md:py-4 relative bg-white/95 dark:bg-black/95 shadow-lg shadow-black/5 backdrop-blur-xl border-b border-black/5 dark:border-white/10`}
        >
          <div
            className={`mx-auto flex items-center justify-between px-4 ${isScrolled > 485 ? "md:px-16" : "container md:px-8"}`}
          >
            {/* Logo */}
            <Link
              href={`/${locale}`}
              className="relative z-60 transition-transform duration-500 hover:scale-[1.02] active:scale-95 shrink-0"
            >
              <Image
                src={theme === "light" ? "/logo.png" : "/light-logo.png"}
                alt="Logo"
                width={180}
                height={45}
                className="h-auto w-28 md:w-32 lg:w-44 brightness-110"
                priority
              />
            </Link>

            {isScrolled < 485 && (
              <span className="hidden md:block">
                <CustomLink locale={locale} />
              </span>
            )}

            {isScrolled > 485 && (
              <div className="hidden md:block max-w-5xl w-full mx-auto xl:block">
                <NavSearchBar />
              </div>
            )}

            {/* Desktop Nav Actions */}
            <nav className="hidden md:flex items-center gap-4 relative z-60">
              <ThemeToggle />
              <LanguageSwitcher variant="hero" />

              {/* User dropdown — replaces the old login Link */}
              <UserDropdown onLoginClick={openLoginModal} />

              <Link
                href={`/${locale}/list-property`}
                className="group relative flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-all duration-300 bg-clay-600 text-white hover:bg-clay-700 hover:shadow-lg hover:shadow-clay-600/25 active:scale-95"
              >
                <span>{listLabel}</span>
                <RiArrowRightLine
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </nav>

            {/* Mobile Right Side: theme + language + hamburger */}
            <div className="flex md:hidden items-center gap-1.5">
              <ThemeToggle />
              <LanguageSwitcher variant="hero" />
              <button
                onClick={toggleMobileMenu}
                aria-label="Open menu"
                className="p-2 rounded-full text-ink-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile slide-in menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        navLinks={navLinks}
        locale={locale}
        loginLabel={loginLabel}
        listLabel={listLabel}
        onClose={closeMobileMenu}
        onLoginClick={openLoginModal}
      />

      {/* Login modal — rendered at header level to overlay everything */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
    </div>
  );
}
